import React, { useState } from 'react'

import {
  BodyText,
  CTALink,
  Button,
  HeadingXSmall,
  IconButton,
  Image,
  InfoIcon,
  Label,
  Notification,
  AppleWalletIcon,
  GoogleWalletIcon,
} from '@pb/components'
import {
  copyToClipboard,
  isIos,
  isAndroid,
  useMediaQuery,
  Breakpoints,
  trackEvent,
} from '@pb/utils'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { ACCOUNT_ROUTES, EXTERNAL_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import useTrackAccountClickEvent from 'features/account/hooks/useTrackAccountClickEvent'
import InlineLink from 'ui/buttons/InlineLink'

import selectors from '../../duck/selectors'
import { postAppleWalletPassRequest, postGoogleWalletPassRequest } from '../../requests'
import styles from './CardBack.scss'
import Props from './types'

const CardBack: React.FC<Props> = ({ currentBalance }) => {
  const codeImageUrl = useSelector(selectors.getCodeImageUrl)
  const codeNumber = useSelector(selectors.getCodeNumber)

  const history = useHistory()
  const trackClickEvent = useTrackAccountClickEvent()
  const trackLinkClick = trackClickEvent(ACCOUNT_TRACKING_EVENTS.memberCardAltIdLinkClick)
  const isAlternativeIdEnabled = !!useSelector(appSelectors.getAlternativeId)?.enabled
  const config = useSelector(appSelectors.getConfig)

  const [shouldShowNotification, setShouldShowNotification] = useState(false)
  const [notificationLabel, setNotificationMessageLabel] = useState('')
  const [shouldShowSpinner, setShouldShowSpinner] = useState(false)
  const isMediumScreen = useMediaQuery(Breakpoints.MEDIUM)

  const trackAppleWalletEvents = (eventAction, statusCode): void =>
    trackEvent({
      event_category: 'myAccount',
      event_type: 'myAccount/memberIDWalletPass',
      event_id: eventAction,
      event_label: statusCode,
    })

  const trackGoogleWalletEvents = (eventAction, statusCode): void =>
    trackEvent({
      event_category: 'myAccount',
      event_type: 'myAccount/memberIDGoogleWalletPass',
      event_id: eventAction,
      event_label: statusCode,
    })
  const handleMemberIdClick = (): void => {
    const isCopiedToClipboard = copyToClipboard(codeNumber)
    setShouldShowNotification(isCopiedToClipboard)
    if (isCopiedToClipboard) {
      setNotificationMessageLabel('my.account.membership.id.copied')
    }
  }
  const features = config?.features
  const appleWalletConfig = features?.appleWallet
  const googleWalletConfig = features?.googleWallet
  const shouldShowAddToAppleWallet =
    isMediumScreen && isIos() && appleWalletConfig?.enabled && Boolean(codeNumber)
  const shouldShowAddToGoogleWallet =
    isMediumScreen && isAndroid() && googleWalletConfig?.enabled && Boolean(codeNumber)

  const handleAddToAppleWalletClick = async (): Promise<void> => {
    trackAppleWalletEvents('passGenerationInitiate', null)
    setShouldShowSpinner(true)
    await postAppleWalletPassRequest()
      .then((jsonRes) => {
        const jsonData = jsonRes.data
        const responseData = jsonData?.data
        if (responseData) {
          const utf8String = new Uint8Array(responseData) // Convert to UTF-8...
          const bufferContent = utf8String.buffer
          const blob = new Blob([bufferContent], {
            type: 'application/vnd.apple.pkpass',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.click()
          setShouldShowSpinner(false)
          trackAppleWalletEvents('passAddedToWallet', 200)
        }
      })
      .catch((error) => {
        setShouldShowNotification(true)
        setNotificationMessageLabel('apple.pass.failure.alert.message')
        trackAppleWalletEvents('passGenerationFailServer', error.response.status)
      })
  }

  const handleAddToGoogleWalletClick = async (): Promise<void> => {
    trackGoogleWalletEvents('passGenerationInitiate', null)
    setShouldShowSpinner(true)
    await postGoogleWalletPassRequest()
      .then((jsonRes) => jsonRes?.data.token)
      .then((url) => {
        window.open(url, '_blank')
        setShouldShowSpinner(false)
        trackGoogleWalletEvents('passAddedToWallet', 200)
      })
      .catch((error) => {
        setShouldShowNotification(true)
        setNotificationMessageLabel('google.pass.failure.alert.message')
        trackGoogleWalletEvents('passGenerationFailServer', error.response.status)
      })
  }
  return (
    <div className={styles.container}>
      <div className={styles.codeContainer}>
        <Image
          className={styles.codeImage}
          url={codeImageUrl}
          altText="Club card code"
          shouldLazyLoad={false}
        />
        <CTALink Tag="button" inline className={styles.codeNumber} onClick={handleMemberIdClick}>
          {codeNumber}
        </CTALink>
      </div>
      <div className={styles.pointsContainer}>
        <BodyText Tag="span" className={styles.pointsInfoText}>
          <Label copyKey="your.points.copy" />
          <IconButton
            onClick={() => history.push(ACCOUNT_ROUTES.PointsHistory.path)}
            Tag="a"
            iconElement={<InfoIcon size="small" />}
            className={styles.pointsInfoButton}
          >
            <Label copyKey="your.points.copy" />
          </IconButton>
        </BodyText>
        <HeadingXSmall Tag="span">{currentBalance}</HeadingXSmall>
      </div>
      {shouldShowAddToAppleWallet &&
        (appleWalletConfig?.imageUrl ? (
          <Button
            primary={false}
            className={styles.addToWalletButton}
            onClick={handleAddToAppleWalletClick}
            shouldShowSpinner={shouldShowSpinner}
          >
            <Image
              className={styles.addToWalletImage}
              altText="Add to apple wallet"
              url={appleWalletConfig?.imageUrl}
            />
          </Button>
        ) : (
          <CTALink
            Tag="button"
            className={styles.addToWalletButton}
            iconElement={<AppleWalletIcon size="xlarge" />}
            iconPosition="start"
            onClick={handleAddToAppleWalletClick}
          />
        ))}
      {shouldShowAddToGoogleWallet &&
        (googleWalletConfig?.imageUrl ? (
          <Button
            primary={false}
            className={styles.addToWalletButton}
            onClick={handleAddToGoogleWalletClick}
            shouldShowSpinner={shouldShowSpinner}
          >
            <Image
              className={styles.addToWalletImage}
              altText="Add to google wallet"
              url={googleWalletConfig?.imageUrl}
            />
          </Button>
        ) : (
          <CTALink
            Tag="button"
            className={styles.addToWalletButton}
            iconElement={<GoogleWalletIcon size="xlarge" />}
            iconPosition="start"
            onClick={handleAddToGoogleWalletClick}
          />
        ))}
      {isAlternativeIdEnabled && (
        <InlineLink
          className={styles.cardAltidLink}
          link={ACCOUNT_ROUTES?.SettingsAlternativeID?.path}
          onClick={trackLinkClick}
        >
          <Label copyKey="account.alternative.id.card.link" />
        </InlineLink>
      )}
      <InlineLink link={EXTERNAL_ROUTES.clubInfoPage}>
        <Label copyKey="about.the.membership" />
      </InlineLink>
      {shouldShowNotification && (
        <Notification
          className={styles.notification}
          type="info"
          timespan={3000}
          onExit={() => setShouldShowNotification(false)}
        >
          <Label copyKey={notificationLabel} />
        </Notification>
      )}
    </div>
  )
}

export default CardBack
